import { onMounted } from "vue";
import type { Ref } from "vue";
import { useRoute } from "vue-router";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { getChainList } from "@/common/graphql/chain";
import { getWordList } from "@/common/graphql/expression";
import searchConditionStore from "@/store/searchConditionStore";
import type { ChainSelectArray, ScheduledDeliveryRouteQuery } from "../types";
import { SCHEDULED_DELIVERY_LIST_FILE_NAME } from "../constants";

/**
 * 画面表示時に必要な初期化処理を提供する composable。
 *
 * @returns 初期化処理を実行する関数群と初期表示に必要な状態
 */
export const useScheduledDeliveryInit = (params: {
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  chainListItems: Ref<ChainSelectArray>;
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  shopSelectToValues: Ref<{ shop_code: string; name: string }>;
  wordList: Record<string, string>;
  onClickListTab: () => Promise<void>;
  onChangeChain: () => Promise<void>;
  onClickSearch: (isUpdateModeBack: () => Promise<boolean>) => Promise<void>;
}) => {
  const route = useRoute();

  /**
   * 編集画面からの戻りか判断する
   * @return true:編集、false:編集以外
   */
  const isUpdateModeBack = async (): Promise<boolean> => {
    const query = route.query as Partial<ScheduledDeliveryRouteQuery>;
    if (query !== null && query !== undefined && query.searchMode) {
      return true;
    } else {
      return false;
    }
  };

  ////////////////////////////////////////////////////////////////////////////////
  // 初期画面
  ////////////////////////////////////////////////////////////////////////////////
  onMounted(async () => {
    try {
      params.overlay.value = true;

      //文言データを取得する
      Object.assign(
        params.wordList,
        await getWordList(
          localStorageGetChainCode(),
          "ScheduledDelivery",
          localStorageGetCustomerCode()
        )
      );

      //チェーン情報を取得する
      params.chainListItems.value = await getChainList(
        localStorageGetCustomerCode()
      );

      //編集画面からの戻り
      const query = route.query as Partial<ScheduledDeliveryRouteQuery>;
      if (query !== null && query !== undefined && query.searchMode) {
        await params.onClickListTab();

        //検索条件を取得する
        const searchCondition: Map<string, string> =
          await searchConditionStore.getCondition(
            SCHEDULED_DELIVERY_LIST_FILE_NAME
          );

        //検索条件を削除する
        await searchConditionStore.clearCondition(
          SCHEDULED_DELIVERY_LIST_FILE_NAME
        );

        if (searchCondition !== null && searchCondition !== undefined) {
          //チェーン情報を設定する
          params.chainSelectValues.value = {
            chain_code: searchCondition.get("chain_code")
              ? (searchCondition.get("chain_code") as string)
              : "",
            name: searchCondition.get("chain_name")
              ? (searchCondition.get("chain_name") as string)
              : "",
          };

          await params.onChangeChain();

          //出荷元店舗情報を設定する
          params.shopSelectFromValues.value = {
            shop_code: searchCondition.get("shop_code_from")
              ? (searchCondition.get("shop_code_from") as string)
              : "",
            name: searchCondition.get("shop_code_from_name")
              ? (searchCondition.get("shop_code_from_name") as string)
              : "",
          };

          //配達先店舗情報を設定する
          params.shopSelectToValues.value = {
            shop_code: searchCondition.get("shop_code_to")
              ? (searchCondition.get("shop_code_to") as string)
              : "",
            name: searchCondition.get("shop_code_to_name")
              ? (searchCondition.get("shop_code_to_name") as string)
              : "",
          };

          //検索
          await params.onClickSearch(isUpdateModeBack);
        }
      }
    } catch (err: unknown) {
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    } finally {
      params.overlay.value = false;
    }
  });

  return {
    isUpdateModeBack,
  };
};
